/* -----------------Globals--------------- */
import { useEffect } from "react";

import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
/* -----------------Helpers & Hooks--------------- */
import { EditorContent, useEditor } from "@tiptap/react";
import useEditorStore from "../../hooks/useEditorStore";

/* -----------------Child components--------------- */

import { events } from "@constants/tracking";
import TTSContainer from "@memorang/features/src/TTS/TTSContainer";
import Box from "@memorang/ui/components/Box";
import Highlight from "@tiptap/extension-highlight";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
/* -----------------Types--------------- */
import StarterKit from "@tiptap/starter-kit";
import { trackCustomEvent } from "analytics";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { Markdown } from "tiptap-markdown";
import { CustomImageExtension } from "../tiptapextensions/ImageExtension";

type Props = {
	text: string;
	handleStoreHtml?: (html: string) => void;
	highlightedHtml?: string;
	isSummativeTest?: boolean;
	fontSize?: number;
	handleClickInsertableText?: (choiceId: string) => void;
	showTTS?: boolean;
	disableInsertableText?: boolean;
	itemId?: string;
};

const htmlEntityReplacements: { searchTerm: string; newValue: string }[] = [
	{ searchTerm: "&gte;", newValue: "&#8805;" },
	{ searchTerm: "&lte;", newValue: "&#8804;" },
	{ searchTerm: "&gt;", newValue: "&#62;" },
	{ searchTerm: "&lt;", newValue: "&#60;" },
	{ searchTerm: "&amp;", newValue: "&" },
];

const ItemStemMarkdownRenderer = ({
	text,
	fontSize = 16,
	handleClickInsertableText,
	showTTS,
	itemId,
	disableInsertableText,
}: {
	text: string;
	fontSize?: number;
	handleClickInsertableText?: (choiceId: string) => void;
	showTTS?: boolean;
	itemId?: string;
	disableInsertableText?: boolean;
}) => {
	const stemFontSize = useAppStore((store) => store.app.fontSizes?.stem);

	const finalStemFontSize = stemFontSize || 16;

	return (
		<Box flexDirection="row" gap={8} width="100%" alignItems="center">
			{!!itemId && showTTS && (
				<TTSContainer
					itemId={itemId}
					eventName={events.ttsClicked}
					trackCustomEvent={trackCustomEvent}
					explanation={text}
					mode={undefined}
					allowPrefetch={true}
				/>
			)}
			<Box flex={1} userSelect="none">
				<CustomMarkdownRenderer
					text={text}
					fontSize={finalStemFontSize || fontSize}
					variant="stem"
					handleClickInsertableText={handleClickInsertableText}
					disableInsertableText={disableInsertableText}
					paragraphMarginVertical={6}
				/>
			</Box>
		</Box>
	);
};

const ItemStemTipTap = ({ text, handleStoreHtml, highlightedHtml }: Props) => {
	const { setEditorRef } = useEditorStore();
	const theme = useAppTheme();
	const stemFontSize = useAppStore((store) => store.app.fontSizes?.stem);
	const appFont = useAppStore((store) => store.app.font) || "opensans";

	const finalStemFontSize = stemFontSize || 16;

	const editor = useEditor({
		extensions: [
			StarterKit,
			Markdown,
			CustomImageExtension,
			Highlight.configure({
				HTMLAttributes: {
					class: "highlight",
				},
			}),
			Table.configure({
				resizable: false,
			}),
			TableRow,
			TableHeader,
			TableCell,
		],
		content: "",
		editable: false,
		onUpdate: ({ editor: updateEditor }) => {
			if (handleStoreHtml) {
				handleStoreHtml(updateEditor.getHTML());
			}
		},
	});

	useEffect(() => {
		if (editor) {
			setEditorRef(editor);
		}
	}, [editor, setEditorRef]);

	useEffect(() => {
		if (editor) {
			let editorText = highlightedHtml || text;
			for (const { searchTerm, newValue } of htmlEntityReplacements) {
				editorText = editorText.replaceAll(searchTerm, newValue);
			}
			editor.commands.setContent(editorText);
		}
	}, [editor, highlightedHtml, text]);

	return (
		<>
			<style>
				{`
					/* Base Styles */
					.ProseMirror {
						font-family: "${appFont}-regular" !important;
						font-size: ${finalStemFontSize}px !important;
						line-height: ${finalStemFontSize * 1.5}px !important;
						color: ${theme.colors.text} !important;
					}
					
					.ProseMirror * {
						color: inherit !important;
					}
					
					.ProseMirror p {
						margin: 0 0 ${(16 * finalStemFontSize) / 14}px 0 !important;
						line-height: ${finalStemFontSize * 1.5}px !important;
						padding: 0 !important;
					}
					
					.ProseMirror p + ul,
					.ProseMirror p + ol {
						margin-top: -${(10 * finalStemFontSize) / 14}px !important;
					}
					
					.ProseMirror strong {
						font-family: "${appFont}-bold" !important;
						font-weight: normal !important;
					}
					
					.ProseMirror ul,
					.ProseMirror ol {
						padding-left: 20px !important;
						margin: 0 0 ${finalStemFontSize * 0.5}px 0 !important;
					}
					
					.ProseMirror ul { list-style-type: disc !important; }
					.ProseMirror ol { list-style-type: decimal !important; }
					
					.ProseMirror li {
						margin-top: ${finalStemFontSize * 0.2}px !important;
						margin-bottom: 0 !important;
						padding-left: 0 !important;
					}
					
					.ProseMirror li:first-child {
						margin-top: 0 !important;
					}
					
					.ProseMirror li > p {
						margin: 0 !important;
						display: inline !important;
					}
					
					.ProseMirror ul ul,
					.ProseMirror ol ul {
						list-style-type: circle !important;
						margin-top: ${finalStemFontSize * 0.2}px !important;
						padding-left: 20px !important;
					}
					
					.ProseMirror ol ol,
					.ProseMirror ul ol {
						list-style-type: lower-alpha !important;
						margin-top: ${finalStemFontSize * 0.2}px !important;
						padding-left: 20px !important;
					}
					
					/* Tables */
					.ProseMirror table {
						border-collapse: collapse;
						table-layout: fixed;
						width: 100%;
						margin: 12px 0;
						border: 1px solid ${theme.colors.outline};
					}
					
					.ProseMirror table td,
					.ProseMirror table th {
						min-width: 1em;
						border: 1px solid ${theme.colors.outline};
						padding: 8px 12px;
						vertical-align: top;
						background: ${theme.colors.surface};
					}
					
					.ProseMirror table th {
						font-family: "${appFont}-bold" !important;
						font-weight: normal;
						background: ${theme.colors.surfaceVariant};
						text-align: center;
					}
					
					.ProseMirror table td {
						text-align: center;
					}
				`}
			</style>
			<EditorContent
				editor={editor}
				aria-label="Item stem"
				aria-labelledby="item-stem"
				title="Item stem"
			/>
		</>
	);
};

const ItemStem = ({
	text,
	handleStoreHtml,
	highlightedHtml,
	fontSize,
	isSummativeTest,
	handleClickInsertableText,
	disableInsertableText,
	itemId,
	showTTS,
}: Props) => {
	//TODO: Need to add highlighting support for markdown renderer, then we can remove tiptap
	if (!isSummativeTest) {
		return (
			<ItemStemMarkdownRenderer
				text={text}
				fontSize={fontSize}
				handleClickInsertableText={handleClickInsertableText}
				disableInsertableText={disableInsertableText}
				itemId={itemId}
				showTTS={showTTS}
			/>
		);
	}

	return (
		<ItemStemTipTap
			text={text}
			handleStoreHtml={handleStoreHtml}
			highlightedHtml={highlightedHtml}
		/>
	);
};

export default ItemStem;
